import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarCheck, Loader2, AlertCircle, MapPin, CheckCircle2,
  ArrowRight, CreditCard, Video, QrCode, Award,
} from "lucide-react";
import { useGetMyEventRegistrationsQuery } from "../api/eventsApi";
import {
  attendanceDisplay,
  ATTENDANCE_TONE_CLASSES,
} from "../utils/attendance";
import { registrationFee, FEE_TONE_CLASSES, type FeeTone } from "../utils/fee";

function fmt(d: string) {
  return new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
}

/** "Oct 14 – Oct 16, 2026", collapsed to one date for a single-day event. */
function dateRange(start?: string, end?: string) {
  if (!start) return "";
  if (!end || end === start) return fmt(start);
  return `${new Date(start).toLocaleDateString("en-NG", { day: "numeric", month: "short" })} – ${fmt(end)}`;
}

/** The date as a card corner reads it: a big day over a small month. */
function dayBox(d?: string) {
  if (!d) return null;
  const date = new Date(d);
  return {
    day:   date.toLocaleDateString("en-NG", { day: "2-digit" }),
    month: date.toLocaleDateString("en-NG", { month: "short" }).toUpperCase(),
  };
}

/**
 * The banner pill, driven by the SAME derivation as the figure below it.
 *
 * Keyed on the fee's tone rather than on payment_status directly: a CPD or
 * legacy back-fill carries the status `free` on an event that charges, and a
 * pill reading "Free" over a figure reading "Paid offline" is the same
 * contradiction this file exists to stop.
 */
const PILL_BY_TONE: Record<FeeTone, { label: string; cls: string }> = {
  due:     { label: "Fee due",    cls: "bg-amber-500 text-white"   },
  settled: { label: "Paid",       cls: "bg-emerald-600 text-white" },
  unknown: { label: "Registered", cls: "bg-gray-500 text-white"    },
};

type Tab = "all" | "upcoming" | "due" | "past";

export default function MyEventsPage() {
  const { data: registrations = [], isLoading, isError } = useGetMyEventRegistrationsQuery();
  const [tab, setTab] = useState<Tab>("all");

  const rows = registrations as any[];

  // An event is still "upcoming" until the day it ENDS has passed — a two-day
  // conference is still on on its second morning.
  const isUpcoming = (r: any) => {
    const end = r.event?.end_date ?? r.event?.start_date;
    if (!end) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(end) >= today;
  };

  const counts = useMemo(() => ({
    all:      rows.length,
    upcoming: rows.filter(isUpcoming).length,
    due:      rows.filter((r) => r.payment_status === "pending").length,
    past:     rows.filter((r) => !isUpcoming(r)).length,
  }), [rows]);

  const filtered = useMemo(() => {
    if (tab === "upcoming") return rows.filter(isUpcoming);
    if (tab === "past")     return rows.filter((r) => !isUpcoming(r));
    if (tab === "due")      return rows.filter((r) => r.payment_status === "pending");
    return rows;
  }, [rows, tab]);

  const cpdEarned = rows.filter((r) => r.cpd_awarded).length;

  const TABS: { key: Tab; label: string }[] = [
    { key: "all",      label: "All"       },
    { key: "upcoming", label: "Upcoming"  },
    { key: "due",      label: "Fee due"   },
    { key: "past",     label: "Past"      },
  ];

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 pb-20 space-y-6">

      {/* ── Heading ─────────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">My Events</h1>
          <p className="text-gray-500 text-sm mt-1 font-medium">
            Your registrations, access cards and receipts.
            {cpdEarned > 0 && ` ${cpdEarned} carried CPD.`}
          </p>
        </div>
        <Link
          to="/events"
          className="inline-flex items-center gap-2 self-start bg-gray-900 text-white font-black text-sm px-5 py-3 rounded-2xl hover:bg-gray-800 transition-colors"
        >
          Browse events <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* ── Tabs, which double as the counts ────────────────────────────── */}
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => {
          const active = tab === t.key;
          const n = counts[t.key];
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-black transition-colors border ${
                active
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
              }`}
            >
              {t.label}
              <span className={`text-[11px] font-black px-1.5 py-0.5 rounded-lg ${
                active ? "bg-white/15 text-white"
                : t.key === "due" && n > 0 ? "bg-amber-100 text-amber-700"
                : "bg-gray-100 text-gray-500"
              }`}>
                {n}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
        </div>
      ) : isError ? (
        <div className="bg-rose-50 border border-rose-100 rounded-3xl p-8 flex items-center gap-4">
          <AlertCircle className="w-6 h-6 text-rose-500 shrink-0" />
          <p className="text-rose-700 font-semibold">Failed to load registrations.</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-3xl p-16 text-center">
          <CalendarCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-bold mb-5">You haven't registered for any events yet.</p>
          <Link
            to="/events"
            className="inline-flex items-center gap-2 bg-gray-900 text-white font-black text-sm px-6 py-3 rounded-2xl hover:bg-gray-800 transition-colors"
          >
            Browse events <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-3xl p-16 text-center">
          <CalendarCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-bold">
            {tab === "due" ? "Nothing outstanding — every fee is settled." : "Nothing here."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filtered.map((reg: any) => <RegistrationCard key={reg.id} reg={reg} />)}
        </div>
      )}
    </div>
  );
}

/* ========================================================================== */

function RegistrationCard({ reg }: { reg: any }) {
  const event      = reg.event ?? {};
  const attendance = attendanceDisplay(reg.overall_attendance, event);
  // One derivation for the pill, the label AND the figure, so no two of them
  // can disagree about whether this delegate paid.
  const fee        = registrationFee(reg, event);
  // A failed charge is its own thing and must not be softened into "Registered".
  // Null on an event with no fee — the pill is then dropped entirely rather
  // than announcing a payment state for money nobody was ever asked for.
  const pay        = reg.payment_status === "failed"
    ? { label: "Failed", cls: "bg-rose-600 text-white" }
    : fee ? PILL_BY_TONE[fee.tone] : null;
  const box        = dayBox(event.start_date);
  const owes       = reg.payment_status === "pending";

  return (
    <Link
      to={`/my-events/${reg.id}`}
      className="group flex flex-col bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-lg hover:border-gray-200 transition-all duration-200"
    >
      {/* Banner */}
      <div className="relative h-40 shrink-0 overflow-hidden">
        {event.background_image ? (
          <img
            src={event.background_image}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-900 via-indigo-900 to-indigo-700 flex items-center justify-center">
            <CalendarCheck className="w-12 h-12 text-white/20" />
          </div>
        )}

        <div className="absolute inset-x-0 top-0 p-3 flex items-start justify-between gap-2">
          {pay ? (
            <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full shadow-sm ${pay.cls}`}>
              {owes ? <CreditCard className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
              {pay.label}
            </span>
          ) : <span />}
          {/* The tone classes carry their own background, so nothing is layered
              under them — two background utilities on one element resolve by
              stylesheet order, not by the order they are written here. */}
          <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border shadow-sm ${ATTENDANCE_TONE_CLASSES[attendance.tone]}`}>
            {attendance.label}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-6">
        <div className="flex items-start justify-between gap-4">
          <h2 className="font-black text-gray-900 text-lg leading-snug line-clamp-2 group-hover:text-indigo-700 transition-colors">
            {event.title ?? "Event"}
          </h2>
          {box && (
            <div className="shrink-0 text-center bg-gray-50 border border-gray-100 rounded-2xl px-3 py-2 leading-none">
              <div className="text-xl font-black text-gray-900">{box.day}</div>
              <div className="text-[10px] font-black text-gray-400 tracking-widest mt-0.5">{box.month}</div>
            </div>
          )}
        </div>

        {event.start_date && (
          <div className="flex items-center gap-2 text-sm text-gray-500 font-medium mt-3">
            <CalendarCheck className="w-4 h-4 text-gray-400 shrink-0" />
            {dateRange(event.start_date, event.end_date)}
          </div>
        )}

        {/* The registration number — what the desk asks for at the gate */}
        <div className="inline-flex items-center gap-2 self-start mt-3 bg-gray-50 border border-gray-100 rounded-xl px-2.5 py-1.5">
          <QrCode className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <span className="font-mono font-bold text-xs text-gray-600">{reg.registration_no}</span>
        </div>

        <div className="flex flex-wrap gap-1.5 mt-3">
          {reg.attendance_mode === "virtual" && (
            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg bg-sky-50 text-sky-700 border border-sky-100">
              <Video className="w-3 h-3" /> Virtual
            </span>
          )}
          {reg.cpd_awarded && (
            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg bg-violet-50 text-violet-700 border border-violet-100">
              <Award className="w-3 h-3" /> CPD awarded
            </span>
          )}
          {reg.price_window_label && (
            <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg bg-gray-50 text-gray-500 border border-gray-100">
              {reg.price_window_label} rate
            </span>
          )}
        </div>

        {/* The fee, given its own line — an outstanding one is the whole point
            of opening the card, so it is named rather than left to a badge. */}
        <div className={`mt-auto pt-4 border-t border-gray-100 flex items-end gap-3 ${fee ? "justify-between" : "justify-end"}`}>
          {fee && (
            <div className="min-w-0">
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">
                {fee.label}
              </div>
              <div className={`text-lg font-black leading-none ${FEE_TONE_CLASSES[fee.tone]}`}>
                {fee.value}
              </div>
            </div>
          )}
          <span className="inline-flex items-center gap-1 text-sm font-black text-indigo-600 group-hover:gap-2 transition-all shrink-0">
            {owes ? "Pay now" : "View card"} <ArrowRight className="w-4 h-4" />
          </span>
        </div>

        {event.location && (
          <div className="flex items-center gap-2 text-xs text-gray-400 font-semibold mt-3">
            <MapPin className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{event.location}</span>
          </div>
        )}
      </div>
    </Link>
  );
}
